import { Autocomplete } from "@mui/material";
import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { Players } from "../../types";
import CustomTextField from "./CustomTextField";

export type BasePlayerSelectorHandle = {
  focus: () => void;
};

export type BasePlayerSelectorProps = {
  label: string;
  currentPlayerId: string | null;
  onPlayerSelect: (player: Players | null) => void;
  players: Players[];
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: Players
  ) => React.ReactNode;
  getOptionLabel?: (option: Players) => string;
  sx?: React.ComponentProps<typeof Autocomplete>["sx"];
};

const BasePlayerSelector = forwardRef<
  BasePlayerSelectorHandle,
  BasePlayerSelectorProps
>(function BasePlayerSelector(
  {
    label,
    onPlayerSelect,
    currentPlayerId,
    players,
    renderOption,
    getOptionLabel = (option: Players) =>
      `${option.name} ${option.surname} - ${option.licenceId}`,
    sx = {},
  },
  ref
) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    },
  }));

  return (
    <Autocomplete
      sx={{ flexGrow: 1, ...sx }}
      disablePortal
      value={players.find((p) => p.id === currentPlayerId) || null}
      autoComplete
      autoHighlight
      autoSelect
      disableClearable={false}
      options={players}
      onChange={(_, value) => {
        onPlayerSelect(value);
      }}
      renderInput={(params) => (
        <CustomTextField
          sx={{ fontSize: 12 }}
          {...params}
          label={label}
          inputRef={inputRef}
        />
      )}
      renderOption={
        renderOption ||
        ((props, option) => <li {...props}>{getOptionLabel(option)}</li>)
      }
      getOptionLabel={getOptionLabel}
      noOptionsText="Aucun joueur trouvé"
      isOptionEqualToValue={(option, value) => option.id === value.id}
    />
  );
});

export default BasePlayerSelector;
